import React, { useMemo } from 'react';
import HeadNext from 'next/head';
import { validateMarketCountryCode } from '@pkgs/components/utils/validation';
import { COUNTRY_LANGUAGE_MAP, decodeProfileValues, DEFAULT_IMAGE, getExposePath, getUploadCareImageUrl, Language } from '@pkgs/api';

import { isIndexedExpose } from '@/utils/propertyDataUtils';
import { getLanguageForCountry } from '@/utils/getLanguageForCountry';
import { useSettings } from '@/hooks/useSettings';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';
import { metaDescriptionsExpose } from '@/consts/metaDescriptions';
import { IndexFollowEnum } from '@/components/common/Head/Head';

import { JsonLdScripts } from './JsonLdScripts';

export const ExposeHead = (): React.ReactElement => {
  const { language, countryCode } = useSettings();
  const { pageVariant, pageLanguages } = useExposePageProps();
  const {
    data: { listing, genericListing, restrictedListing },
  } = useListing();

  const profile = decodeProfileValues(genericListing.profile);
  const { title, description: profileDescription } = profile;

  // Use blurred image for social media previews when listing is restricted
  const isRestrictedListing = !!restrictedListing;
  let imageUrl = DEFAULT_IMAGE;
  if (isRestrictedListing) {
    imageUrl = getUploadCareImageUrl({
      imageId: restrictedListing.uploadCareImages?.[0]?.id,
      useWaterMark: false,
      isBlurred: true,
    });
  } else if (genericListing.propertyImages?.[0]?.id) {
    imageUrl = getUploadCareImageUrl({
      imageId: genericListing.propertyImages[0].id,
      useWaterMark: listing?.hasWaterMark,
    });
  }

  const description = metaDescriptionsExpose(profileDescription);
  const isIndexed = isIndexedExpose({
    listing,
    pageVariant,
    countryCode,
    language,
    pageLanguages,
  });

  const validatedCountryCode = validateMarketCountryCode(listing?.countryAlpha2) ? listing.countryAlpha2 : countryCode;
  const languageForCountry = getLanguageForCountry(validatedCountryCode, language);

  const canonicalPath = getExposePath(genericListing, languageForCountry, validatedCountryCode);

  const availableLanguages = COUNTRY_LANGUAGE_MAP.get(validatedCountryCode)?.languages.filter(
    (language) => listing?.languages?.includes(language) && language !== Language.ca,
  );

  const paths = useMemo(() => {
    if (isIndexed && availableLanguages) {
      return availableLanguages.map((language) => ({
        language,
        path: getExposePath(genericListing, language, validatedCountryCode),
      }));
    }
    return [];
  }, [isIndexed, availableLanguages, genericListing, validatedCountryCode]);

  return (
    <HeadNext>
      <title>{title}</title>
      {Boolean(description) && <meta name="description" content={description} />}
      <meta property="og:title" content={title} />
      <meta property="og:locale" content={language} />
      <meta property="og:site_name" content="Engel &amp; Völkers" />
      <meta name="robots" content={isIndexed ? IndexFollowEnum.INDEXFOLLOW : IndexFollowEnum.NOINDEXNOFOLLOW} />
      {Boolean(description) && <meta property="og:description" content={description} />}
      {Boolean(imageUrl) && <meta property="og:image" content={imageUrl} />}
      {Boolean(canonicalPath) && <link rel="canonical" href={`https://www.engelvoelkers.com${canonicalPath}`} />}
      {paths.map((path) => {
        return <link key={path.path} rel="alternate" hrefLang={path.language} href={`https://www.engelvoelkers.com${path.path}`} />;
      })}
      {listing && <JsonLdScripts listing={listing} title={title} description={description} imageUrl={imageUrl} />}
    </HeadNext>
  );
};
